import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { TicketPriority, TicketStatus } from '../../../models/Support/SupportEnums';
import { getUserReferenceName } from '../../../utils/Support/supportHelpers';
import type { MultiColorPillProps } from '../../../web/components-v2/Pill';
import { styles } from './styles';

/**
 * Page-local presentation helpers shared by the inbox panes: score labels for the AI signals,
 * the status → pill mapping, and the timestamp formats used across the list, timeline, and
 * details surfaces.
 */

export type TicketPillColor = MultiColorPillProps['color'];

export const getComplexityInfo = (value: number) => {
	if (value <= 33) return { label: 'Simple', style: styles.aiScoreGreen };
	if (value <= 66) return { label: 'Moderate', style: styles.aiScoreYellow };
	return { label: 'Complex', style: styles.aiScoreRed };
};

export const getConfidenceInfo = (value: number) => {
	if (value <= 33) return { label: 'Low', style: styles.aiScoreRed };
	if (value <= 66) return { label: 'Medium', style: styles.aiScoreYellow };
	return { label: 'High', style: styles.aiScoreGreen };
};

/** Display label + pill color for a ticket status. New and Open both read as "Active" to agents. */
export const getStatusInfo = (status: TicketStatus): { label: string; color: TicketPillColor } => {
	if (status === TicketStatus.New || status === TicketStatus.Open) return { label: 'Active', color: 'green' };
	if (status === TicketStatus.Pending) return { label: 'Pending', color: 'yellow' };
	if (status === TicketStatus.Spam) return { label: 'Spam', color: 'gray' };
	return { label: 'Closed', color: 'gray' };
};

export const getTicketContactLabel = (ticket: { contactName?: string; contactEmail?: string }): string =>
	ticket.contactName || ticket.contactEmail || '—';

export const getTicketAssigneeName = (
	ticket: Pick<ISupportTicket, 'assignee' | 'assigneeId'>,
	agentNameMap: Record<string, string>
): string | undefined =>
	getUserReferenceName(ticket.assignee, { includeEmail: false }) ||
	(ticket.assigneeId ? agentNameMap[ticket.assigneeId] : undefined) ||
	ticket.assignee?.email;

/**
 * Desk-list ordering: active tickets (we owe a reply) float above pending ones (waiting on the
 * customer). Applied per fetched page — cross-page ordering stays a server concern.
 */
export const sinkPendingTickets = (tickets: ISupportTicket[]): ISupportTicket[] => [
	...tickets.filter(ticket => ticket.status !== TicketStatus.Pending),
	...tickets.filter(ticket => ticket.status === TicketStatus.Pending),
];

/** Pill color per ticket priority, shared by the desk list rows and the table view. */
export const PRIORITY_COLOR: Record<TicketPriority, TicketPillColor> = {
	[TicketPriority.Low]: 'gray',
	[TicketPriority.Normal]: 'blue',
	[TicketPriority.High]: 'orange',
	[TicketPriority.Urgent]: 'red',
};

/** Compact "2m / 3h / 2d / date" timestamp for the tight list column. */
export const formatRelativeTime = (iso: string): string => {
	if (!iso) return '—';
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return '—';
	const diffMin = Math.floor(Math.max(0, Date.now() - then) / 60_000);
	if (diffMin < 1) return 'now';
	if (diffMin < 60) return `${diffMin}m`;
	const diffHr = Math.floor(diffMin / 60);
	if (diffHr < 24) return `${diffHr}h`;
	const diffDays = Math.floor(diffHr / 24);
	if (diffDays < 7) return `${diffDays}d`;
	return new Date(iso).toLocaleDateString();
};

export const formatShortDateTime = (iso: string | undefined): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Locale date + time for thread/event timestamps; em dash when the value is missing or unparseable. */
export const formatDateTime = (iso: string | undefined): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleString();
};

/** Locale date (no time) for compact metadata rows; em dash when the value is missing or unparseable. */
export const formatDate = (iso: string | undefined): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleDateString();
};
